import { Link } from "react-router-dom";
import { Star, Users, TrendingUp, CheckCircle2, ArrowRight, Quote } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { studioInfo, benefits, testimonials } from "../data/mock";

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-20 min-h-screen flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxneW0lMjBmaXRuZXNzfGVufDB8fHx8MTc3MjAwMzE2NXww&ixlib=rb-4.1.0&q=85"
            alt="Gym"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/60"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-[#9FFF00] fill-[#9FFF00]" />
                ))}
              </div>
              <span className="text-white font-semibold">{studioInfo.rating} Rating</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight animate-in fade-in slide-in-from-bottom-4 duration-700 delay-100">
              Where Fitness Meets{" "}
              <span className="text-[#9FFF00]">Community</span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-300 mb-8 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
              Transform your life with personalized training, unwavering support, and a community
              that celebrates every victory with you.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300">
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-[#9FFF00] hover:bg-[#8AE600] text-black font-bold px-8 py-6 text-lg group"
                >
                  Book a Session
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/services">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-gray-900 font-semibold px-8 py-6 text-lg"
                >
                  Explore Services
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose GetFit?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We're more than a gym - we're your partner in transformation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = { Users, TrendingUp, CheckCircle2 }[benefit.icon];
              return (
                <Card
                  key={index}
                  className="border-2 hover:border-[#9FFF00] transition-all hover:shadow-xl group"
                >
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#9FFF00] to-[#8AE600] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Your Fitness Journey Starts Here
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                At GetFit Fitness Studio, we believe that fitness is personal. That's why we offer
                individualized training programs tailored to your unique goals, fitness level, and
                lifestyle.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our boutique studio in Malmesbury provides an intimate, supportive environment
                where everyone is welcome. We're proud to create an inclusive space that
                celebrates diversity and builds genuine community.
              </p>
              <Link to="/about">
                <Button className="bg-[#9FFF00] text-black hover:bg-[#8AE600] text-white font-semibold group">
                  Learn More About Us
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1648542036561-e1d66a5ae2b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxwZXJzb25hbCUyMHRyYWluaW5nfGVufDB8fHx8MTc3MjAwMzE2Mnww&ixlib=rb-4.1.0&q=85"
                alt="Personal Training"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center gap-2 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-black fill-[#9FFF00]" />
                  ))}
                </div>
                <p className="text-2xl font-bold text-gray-900">{studioInfo.rating}</p>
                <p className="text-sm text-gray-600">Client Rating</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What Our Members Say
            </h2>
            <p className="text-xl text-gray-600">Real results from real people</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card
                key={testimonial.id}
                className="border-2 hover:border-[#9FFF00] transition-all hover:shadow-xl"
              >
                <CardContent className="p-8">
                  <Quote className="w-10 h-10 text-black mb-4" />
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-black fill-[#9FFF00]" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                  <div className="flex items-center gap-3">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-600">GetFit Member</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/reviews">
              <Button variant="outline" className="border-2 border-[#9FFF00] text-black hover:bg-[#9FFF00] text-black hover:text-white font-semibold">
                Read More Reviews
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#9FFF00] to-[#8AE600] text-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Transform Your Life?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto font-medium">
            Join the GetFit community today and discover what you're truly capable of achieving.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-black text-[#9FFF00] hover:bg-gray-900 font-bold px-8 py-6 text-lg"
            >
              Book Your Free Consultation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
